/**
 * Brand Product Form Component
 * Modern form for adding/editing products - works within DashboardLayout
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useBrand } from '../context/BrandContext';
import { ArrowLeft, Save, X, Upload, Image, Link as LinkIcon, Instagram, Globe, Package, Camera } from 'lucide-react';
import { getAllCurrencies, getPopularCurrencies, searchCurrencies } from '../services/currencyService';

function BrandProductForm() {
  const navigate = useNavigate();
  const { productId } = useParams();
  const isEditing = !!productId;
  
  const { brand, isAuthenticated, loading, authFetch } = useBrand();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    price: '',
    currency: 'INR',
    description: '',
    image: '',
    additional_images: [],
    link: '',
    link_type: 'instagram',
    categories: '',
    is_out_of_stock: false
  });
  const [imagePreview, setImagePreview] = useState('');
  const [additionalPreviews, setAdditionalPreviews] = useState([]);
  const [loadingProduct, setLoadingProduct] = useState(isEditing);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingAdditional, setUploadingAdditional] = useState(false);
  const additionalFileInputRef = useRef(null);
  const additionalCameraInputRef = useRef(null);
  const [additionalUrlInput, setAdditionalUrlInput] = useState('');
  const [isDraggingAdditional, setIsDraggingAdditional] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [allCurrencies, setAllCurrencies] = useState([]);
  const [currencySearch, setCurrencySearch] = useState('');

  // Load currencies
  useEffect(() => {
    setAllCurrencies(getAllCurrencies());
  }, []);

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/brand-portal/login');
    }
  }, [isAuthenticated, loading, navigate]);

  // Set default link type based on brand
  useEffect(() => {
    if (brand && !isEditing) {
      setFormData(prev => ({
        ...prev,
        currency: brand.default_currency || 'INR',
        link_type: brand.has_website ? 'website' : 'instagram'
      }));
    }
  }, [brand, isEditing]);

  // Fetch product if editing
  useEffect(() => {
    const fetchProduct = async () => {
      if (!isAuthenticated || !productId) return;
      
      try {
        const response = await authFetch(`/api/brand/products/${productId}/`);
        const data = await response.json();

        if (response.ok) {
          setFormData({
            title: data.title || '',
            price: data.price?.toString() || '',
            currency: data.currency || 'INR',
            description: data.description || '',
            image: data.image || '',
            additional_images: data.additional_images || [],
            link: data.link || '',
            link_type: data.link_type || 'instagram',
            categories: Array.isArray(data.categories) ? data.categories.join(', ') : (data.categories || ''),
            is_out_of_stock: data.is_out_of_stock || false
          });
          setImagePreview(data.image || '');
          setAdditionalPreviews(data.additional_images || []);
        } else {
          setError(data.error || 'Failed to load product');
        }
      } catch (err) {
        setError('Failed to load product');
      } finally {
        setLoadingProduct(false);
      }
    };

    if (isEditing) {
      fetchProduct();
    }
  }, [isAuthenticated, productId, authFetch, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setValidationErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setFormData(prev => ({ ...prev, image: url }));
    setImagePreview(url);
    setValidationErrors(prev => ({ ...prev, image: '' }));
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setValidationErrors(prev => ({ ...prev, image: 'Invalid image type. Use JPEG, PNG, WebP, or GIF.' }));
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      setValidationErrors(prev => ({ ...prev, image: 'Image too large. Maximum size is 5MB.' }));
      return;
    }
    
    setUploadingImage(true);
    setValidationErrors(prev => ({ ...prev, image: '' }));
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);
      
      const response = await authFetch('/api/brand/upload-image/', {
        method: 'POST',
        body: formDataUpload
      });
      
      const data = await response.json();
      
      if (response.ok && data.url) {
        setFormData(prev => ({ ...prev, image: data.url }));
        setImagePreview(data.url);
      } else {
        setValidationErrors(prev => ({ ...prev, image: data.error || 'Failed to upload image' }));
      }
    } catch (err) {
      setValidationErrors(prev => ({ ...prev, image: 'Failed to upload image' }));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging false if we're leaving the drop zone itself
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleCameraCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image: '' }));
    setImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAdditionalImageUpload = async (file) => {
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setValidationErrors(prev => ({ ...prev, additional_images: 'Invalid image type. Use JPEG, PNG, WebP, or GIF.' }));
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      setValidationErrors(prev => ({ ...prev, additional_images: 'Image too large. Maximum size is 5MB.' }));
      return;
    }
    
    setUploadingAdditional(true);
    setValidationErrors(prev => ({ ...prev, additional_images: '' }));
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);
      
      const response = await authFetch('/api/brand/upload-image/', {
        method: 'POST',
        body: formDataUpload
      });
      
      const data = await response.json();
      
      if (response.ok && data.url) {
        setFormData(prev => ({
          ...prev,
          additional_images: [...prev.additional_images, data.url]
        }));
        setAdditionalPreviews(prev => [...prev, data.url]);
      } else {
        setValidationErrors(prev => ({ ...prev, additional_images: data.error || 'Failed to upload image' }));
      }
    } catch (err) {
      setValidationErrors(prev => ({ ...prev, additional_images: 'Failed to upload image' }));
    } finally {
      setUploadingAdditional(false);
    }
  };

  const handleAdditionalFileSelect = (e) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach(file => handleAdditionalImageUpload(file));
    }
    if (additionalFileInputRef.current) {
      additionalFileInputRef.current.value = '';
    }
  };

  const removeAdditionalImage = (index) => {
    setFormData(prev => ({
      ...prev,
      additional_images: prev.additional_images.filter((_, i) => i !== index)
    }));
    setAdditionalPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleAdditionalCameraCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) handleAdditionalImageUpload(file);
  };

  const handleAdditionalUrlAdd = () => {
    const url = additionalUrlInput.trim();
    if (!url) return;
    if (formData.additional_images.length >= 5) {
      setValidationErrors(prev => ({ ...prev, additional_images: 'Maximum 5 additional images allowed' }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      additional_images: [...prev.additional_images, url]
    }));
    setAdditionalPreviews(prev => [...prev, url]);
    setAdditionalUrlInput('');
  };

  const handleAdditionalDragOver = (e) => {
    e.preventDefault();
    setIsDraggingAdditional(true);
  };

  const handleAdditionalDragLeave = (e) => {
    e.preventDefault();
    setIsDraggingAdditional(false);
  };

  const handleAdditionalDrop = (e) => {
    e.preventDefault();
    setIsDraggingAdditional(false);
    const files = e.dataTransfer.files;
    if (files) {
      Array.from(files).forEach(file => handleAdditionalImageUpload(file));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = 'Product title is required';
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      errors.price = 'Valid price is required';
    }

    if (!formData.image.trim()) {
      errors.image = 'Product image is required';
    }

    if (!formData.link.trim()) {
      errors.link = 'Purchase link is required';
    } else {
      try {
        new URL(formData.link);
      } catch {
        errors.link = 'Please enter a valid URL';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setError('');

    try {
      const endpoint = isEditing 
        ? `/api/brand/products/${productId}/`
        : '/api/brand/products/';
      
      const method = isEditing ? 'PATCH' : 'POST';
      
      // Convert comma-separated categories string to array
      const categoriesArray = formData.categories
        ? formData.categories.split(',').map(cat => cat.trim()).filter(Boolean)
        : [];
      
      const response = await authFetch(endpoint, {
        method,
        body: JSON.stringify({
          title: formData.title.trim(),
          price: parseFloat(formData.price),
          currency: formData.currency,
          description: formData.description.trim(),
          image: formData.image.trim(),
          additional_images: formData.additional_images || [],
          link: formData.link.trim(),
          link_type: formData.link_type,
          categories: categoriesArray,
          is_out_of_stock: formData.is_out_of_stock
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        navigate('/brand-portal/collection');
      } else {
        setError(data.error || `Failed to ${isEditing ? 'update' : 'create'} product`);
      }
    } catch (err) {
      setError(`Failed to ${isEditing ? 'update' : 'create'} product`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || loadingProduct) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner} />
        <span style={{ color: '#71717a' }}>Loading...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={styles.header}>
        <Link to="/brand-portal/collection" style={styles.backLink}>
          <ArrowLeft style={{ width: '16px', height: '16px' }} />
          Back to Products
        </Link>
        <div style={styles.label}>
          {isEditing ? 'E D I T I N G' : 'N E W   P R O D U C T'}
        </div>
        <h1 style={styles.title}>{isEditing ? 'Edit Product' : 'Add Product'}</h1>
      </div>

      {error && (
        <div style={styles.errorAlert}>
          <span>{error}</span>
          <button onClick={() => setError('')} style={styles.alertClose}>
            <X style={{ width: '16px', height: '16px' }} />
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Product Details */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.cardIcon}>
              <Package style={{ width: '20px', height: '20px', color: '#171717' }} />
            </div>
            <div>
              <h3 style={styles.cardTitle}>Product Details</h3>
              <p style={styles.cardSubtitle}>Basic information about your product</p>
            </div>
          </div>

          <div style={styles.formSection}>
            {/* Title */}
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                Product Title <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                name="title"
                placeholder="e.g., Handwoven Pashmina Shawl"
                value={formData.title}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  borderColor: validationErrors.title ? '#fecaca' : '#e5e5e5',
                }}
              />
              {validationErrors.title && (
                <p style={styles.errorText}>{validationErrors.title}</p>
              )}
            </div>

            {/* Price & Currency */}
            <div className="bp-two-column" style={styles.twoColumn}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Price <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="number"
                  name="price"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.price}
                  onChange={handleChange}
                style={{
                  ...styles.input,
                  borderColor: validationErrors.price ? '#fecaca' : '#e5e5e5',
                }}
                />
                {validationErrors.price && (
                  <p style={styles.errorText}>{validationErrors.price}</p>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Currency</label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  style={{ ...styles.input, ...styles.select }}
                >
                  <optgroup label="Popular Currencies">
                    {getPopularCurrencies().map(currency => (
                      <option key={currency.code} value={currency.code}>
                        {currency.symbol} {currency.code} - {currency.name}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="All Currencies">
                    {allCurrencies
                      .filter(currency => !getPopularCurrencies().some(pop => pop.code === currency.code))
                      .map(currency => (
                        <option key={currency.code} value={currency.code}>
                          {currency.symbol} {currency.code} - {currency.name}
                        </option>
                      ))}
                  </optgroup>
                </select>
              </div>
            </div>

            {/* Description */}
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Description</label>
              <textarea
                name="description"
                placeholder="Describe your product's features, materials, and craftsmanship..."
                value={formData.description}
                onChange={handleChange}
                rows="4"
                style={{ ...styles.input, ...styles.textarea }}
              />
            </div>

            {/* Categories */}
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Categories</label>
              <input
                type="text"
                name="categories"
                placeholder="e.g., Accessories, Jewelry, Clothing (comma-separated)"
                value={formData.categories}
                onChange={handleChange}
                style={styles.input}
              />
              <p style={styles.helpText}>
                Enter categories separated by commas to help organize your products
              </p>
            </div>
          </div>
        </div>

        {/* Product Images */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.cardIcon}>
              <Image style={{ width: '20px', height: '20px', color: '#171717' }} />
            </div>
            <div>
              <h3 style={styles.cardTitle}>Product Images</h3>
              <p style={styles.cardSubtitle}>Upload a main image and additional photos</p>
            </div>
          </div>

          <div style={styles.formSection}>
            {/* Main Image Label */}
            <label style={{ ...styles.formLabel, marginBottom: '12px' }}>
              Main Image <span style={{ color: '#ef4444' }}>*</span>
            </label>

            {imagePreview ? (
              <div style={styles.imagePreviewContainer}>
                <img src={imagePreview} alt="Preview" style={styles.imagePreview} />
                <button type="button" onClick={removeImage} style={styles.removeImageBtn}>
                  <X style={{ width: '16px', height: '16px' }} />
                  Remove
                </button>
              </div>
            ) : (
              <div
                style={{
                  ...styles.dropZone,
                  border: `2px dashed ${isDragging ? '#171717' : '#e5e5e5'}`,
                  background: isDragging ? '#fafafa' : '#ffffff',
                }}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleCameraCapture}
                  style={{ display: 'none' }}
                />
                {uploadingImage ? (
                  <div style={styles.uploadingState}>
                    <div style={styles.spinner} />
                    <p style={{ margin: 0, color: '#71717a' }}>Uploading...</p>
                  </div>
                ) : (
                  <div style={styles.dropZoneContent}>
                    <Upload style={{ width: '40px', height: '40px', color: '#a1a1aa', marginBottom: '16px' }} />
                    <p style={{ margin: '0 0 4px', color: '#171717' }}>
                      <strong>Click to upload</strong> or drag and drop
                    </p>
                    <p style={{ margin: 0, fontSize: '13px', color: '#a1a1aa' }}>
                      PNG, JPG, WebP or GIF (max. 5MB)
                    </p>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); cameraInputRef.current?.click(); }}
                      style={styles.cameraBtn}
                    >
                      <Camera style={{ width: '16px', height: '16px' }} />
                      Take Photo
                    </button>
                  </div>
                )}
              </div>
            )}

            <div style={styles.divider}>
              <div style={styles.dividerLine} />
              <span style={styles.dividerText}>or</span>
              <div style={styles.dividerLine} />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Paste image URL</label>
              <input
                type="url"
                name="image"
                placeholder="https://example.com/your-product-image.jpg"
                value={formData.image}
                onChange={handleImageUrlChange}
                style={{
                  ...styles.input,
                  borderColor: validationErrors.image ? '#fecaca' : '#e5e5e5',
                }}
              />
              {validationErrors.image && (
                <p style={styles.errorText}>{validationErrors.image}</p>
              )}
            </div>

            {/* Additional Images Section */}
            <div style={{ marginTop: '32px', paddingTop: '32px', borderTop: '1px solid #f5f5f5' }}>
              <label style={{ ...styles.formLabel, marginBottom: '12px' }}>
                Additional Images <span style={{ color: '#a1a1aa', fontWeight: '400', textTransform: 'none', letterSpacing: 'normal' }}>(optional, up to 5)</span>
              </label>

              {/* Existing additional image previews */}
              {additionalPreviews.length > 0 && (
                <div style={{ ...styles.additionalImagesGrid, marginBottom: '16px' }}>
                  {additionalPreviews.map((url, index) => (
                    <div key={index} style={styles.additionalImageItem}>
                      <img src={url} alt={`Additional ${index + 1}`} style={styles.additionalImagePreview} />
                      <button
                        type="button"
                        onClick={() => removeAdditionalImage(index)}
                        style={styles.additionalRemoveBtn}
                      >
                        <X style={{ width: '14px', height: '14px' }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload / Camera / URL area — only show if under limit */}
              {additionalPreviews.length < 5 && (
                <>
                  <div
                    style={{
                      ...styles.dropZone,
                      border: `2px dashed ${isDraggingAdditional ? '#171717' : '#e5e5e5'}`,
                      background: isDraggingAdditional ? '#fafafa' : '#ffffff',
                      padding: '24px',
                    }}
                    onClick={() => additionalFileInputRef.current?.click()}
                    onDragOver={handleAdditionalDragOver}
                    onDragLeave={handleAdditionalDragLeave}
                    onDrop={handleAdditionalDrop}
                  >
                    <input
                      ref={additionalFileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      multiple
                      onChange={handleAdditionalFileSelect}
                      style={{ display: 'none' }}
                    />
                    <input
                      ref={additionalCameraInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleAdditionalCameraCapture}
                      style={{ display: 'none' }}
                    />
                    {uploadingAdditional ? (
                      <div style={styles.uploadingState}>
                        <div style={styles.spinner} />
                        <p style={{ margin: 0, color: '#71717a' }}>Uploading...</p>
                      </div>
                    ) : (
                      <div style={styles.dropZoneContent}>
                        <Upload style={{ width: '32px', height: '32px', color: '#a1a1aa', marginBottom: '12px' }} />
                        <p style={{ margin: '0 0 4px', color: '#171717', fontSize: '14px' }}>
                          <strong>Click to upload</strong> or drag and drop
                        </p>
                        <p style={{ margin: 0, fontSize: '12px', color: '#a1a1aa' }}>
                          PNG, JPG, WebP or GIF (max. 5MB) · {5 - additionalPreviews.length} remaining
                        </p>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); additionalCameraInputRef.current?.click(); }}
                          style={styles.cameraBtn}
                        >
                          <Camera style={{ width: '16px', height: '16px' }} />
                          Take Photo
                        </button>
                      </div>
                    )}
                  </div>

                  <div style={styles.divider}>
                    <div style={styles.dividerLine} />
                    <span style={styles.dividerText}>or</span>
                    <div style={styles.dividerLine} />
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="url"
                      placeholder="https://example.com/another-product-image.jpg"
                      value={additionalUrlInput}
                      onChange={(e) => setAdditionalUrlInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdditionalUrlAdd(); } }}
                      style={{ ...styles.input, flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={handleAdditionalUrlAdd}
                      style={{
                        padding: '10px 20px',
                        background: '#171717',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'background 0.15s',
                      }}
                    >
                      Add URL
                    </button>
                  </div>
                </>
              )}

              {validationErrors.additional_images && (
                <p style={styles.errorText}>{validationErrors.additional_images}</p>
              )}
              <p style={styles.helpText}>
                Add more photos to showcase different angles and details of your product
              </p>
            </div>
          </div>
        </div>

        {/* Purchase Link */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.cardIcon}>
              <LinkIcon style={{ width: '20px', height: '20px', color: '#171717' }} />
            </div>
            <div>
              <h3 style={styles.cardTitle}>Purchase Link</h3>
              <p style={styles.cardSubtitle}>Where customers can buy this product</p>
            </div>
          </div>

          <div style={styles.formSection}>
            <div style={styles.linkTypeButtons}>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, link_type: 'instagram' }))}
                style={{
                  ...styles.linkTypeBtn,
                  ...(formData.link_type === 'instagram' ? styles.linkTypeBtnActive : {}),
                }}
              >
                <Instagram style={{ width: '16px', height: '16px' }} />
                Instagram
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, link_type: 'website' }))}
                style={{
                  ...styles.linkTypeBtn,
                  ...(formData.link_type === 'website' ? styles.linkTypeBtnActive : {}),
                }}
              >
                <Globe style={{ width: '16px', height: '16px' }} />
                Website
              </button>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>
                {formData.link_type === 'instagram' ? 'Instagram URL' : 'Product URL'} <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="url"
                name="link"
                placeholder={formData.link_type === 'instagram' 
                  ? 'https://instagram.com/p/...' 
                  : 'https://yoursite.com/product/...'
                }
                value={formData.link}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  borderColor: validationErrors.link ? '#fecaca' : '#e5e5e5',
                }}
              />
              {validationErrors.link && (
                <p style={styles.errorText}>{validationErrors.link}</p>
              )}
              <p style={styles.helpText}>
                {formData.link_type === 'instagram' 
                  ? 'Customers will be directed to your Instagram post or profile'
                  : 'Customers will be directed to this product page'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Stock Status */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={{ ...styles.cardIcon, background: formData.is_out_of_stock ? '#fef2f2' : '#f0fdf4' }}>
              <Package style={{ width: '20px', height: '20px', color: formData.is_out_of_stock ? '#ef4444' : '#22c55e' }} />
            </div>
            <div>
              <h3 style={styles.cardTitle}>Stock Status</h3>
              <p style={styles.cardSubtitle}>Let customers know if this product is available</p>
            </div>
          </div>
          <div style={styles.formSection}>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, is_out_of_stock: !prev.is_out_of_stock }))}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                width: '100%',
                padding: '16px 20px',
                border: `1px solid ${formData.is_out_of_stock ? '#fecaca' : '#bbf7d0'}`,
                borderRadius: '10px',
                background: formData.is_out_of_stock ? '#fef2f2' : '#f0fdf4',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s',
              }}
            >
              <div style={{
                width: '44px',
                height: '24px',
                borderRadius: '999px',
                background: formData.is_out_of_stock ? '#ef4444' : '#22c55e',
                position: 'relative',
                flexShrink: 0,
                transition: 'background 0.2s',
              }}>
                <div style={{
                  position: 'absolute',
                  top: '3px',
                  left: formData.is_out_of_stock ? '23px' : '3px',
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  background: '#fff',
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
              <div>
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#171717', marginBottom: '2px' }}>
                  {formData.is_out_of_stock ? 'Out of Stock' : 'In Stock'}
                </div>
                <div style={{ fontSize: '12px', color: '#71717a' }}>
                  {formData.is_out_of_stock
                    ? 'Customers will see an "Out of Stock" badge on this product'
                    : 'This product is available for customers to view and purchase'}
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Submit Actions */}
        <div className="bp-form-actions" style={styles.formActions}>
          <Link to="/brand-portal/collection" style={styles.cancelBtn}>
            Cancel
          </Link>
          <button type="submit" disabled={isSubmitting} style={styles.submitBtn}>
            {isSubmitting ? (
              <>
                <div style={styles.buttonSpinner} />
                {isEditing ? 'Saving...' : 'Adding...'}
              </>
            ) : (
              <>
                <Save style={{ width: '16px', height: '16px' }} />
                {isEditing ? 'Save Changes' : 'Add Product'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

const styles = {
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
    gap: '16px',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '2px solid #e5e5e5',
    borderTopColor: '#171717',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  header: {
    marginBottom: '32px',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: '#71717a',
    textDecoration: 'none',
    marginBottom: '16px',
  },
  label: {
    fontSize: '12px',
    letterSpacing: '0.3em',
    color: '#a1a1aa',
    marginBottom: '8px',
  },
  title: {
    fontSize: '42px',
    fontWeight: '400',
    color: '#171717',
    margin: 0,
    letterSpacing: '-0.02em',
  },
  errorAlert: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#ef4444',
    padding: '16px 20px',
    borderRadius: '12px',
    marginBottom: '24px',
    fontSize: '14px',
  },
  alertClose: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#ef4444',
    padding: '4px',
  },
  card: {
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '16px',
    marginBottom: '24px',
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '24px 32px',
    borderBottom: '1px solid #f5f5f5',
  },
  cardIcon: {
    width: '44px',
    height: '44px',
    background: '#f5f5f5',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '500',
    color: '#171717',
    margin: 0,
  },
  cardSubtitle: {
    fontSize: '13px',
    color: '#71717a',
    margin: '4px 0 0',
  },
  formSection: {
    padding: '32px',
  },
  formGroup: {
    marginBottom: '24px',
  },
  formLabel: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '500',
    color: '#71717a',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    fontSize: '15px',
    color: '#171717',
    outline: 'none',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  },
  select: {
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2371717a' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 16px center',
  },
  textarea: {
    resize: 'vertical',
    minHeight: '120px',
    lineHeight: '1.6',
  },
  twoColumn: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '24px',
  },
  errorText: {
    marginTop: '8px',
    fontSize: '13px',
    color: '#ef4444',
  },
  helpText: {
    marginTop: '8px',
    fontSize: '13px',
    color: '#a1a1aa',
  },
  imagePreviewContainer: {
    position: 'relative',
    maxWidth: '400px',
  },
  imagePreview: {
    width: '100%',
    aspectRatio: '1',
    objectFit: 'cover',
    borderRadius: '12px',
    border: '1px solid #e5e5e5',
  },
  removeImageBtn: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    background: 'rgba(255,255,255,0.95)',
    backdropFilter: 'blur(4px)',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#171717',
    cursor: 'pointer',
  },
  dropZone: {
    border: '2px dashed',
    borderRadius: '12px',
    padding: '48px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  dropZoneContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  uploadingState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    margin: '24px 0',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    background: '#e5e5e5',
  },
  dividerText: {
    fontSize: '13px',
    color: '#a1a1aa',
  },
  cameraBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '16px',
    padding: '10px 20px',
    background: '#f5f5f5',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#171717',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  linkTypeButtons: {
    display: 'flex',
    gap: '12px',
    marginBottom: '24px',
  },
  linkTypeBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '14px',
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#171717',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  linkTypeBtnActive: {
    background: '#171717',
    borderColor: '#171717',
    color: '#ffffff',
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    paddingTop: '8px',
  },
  cancelBtn: {
    display: 'flex',
    alignItems: 'center',
    padding: '14px 24px',
    background: '#ffffff',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#171717',
    textDecoration: 'none',
    cursor: 'pointer',
  },
  submitBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '14px 24px',
    background: '#171717',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#ffffff',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  buttonSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid #ffffff',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  additionalImagesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: '12px',
    marginBottom: '12px',
  },
  additionalImageItem: {
    position: 'relative',
    aspectRatio: '1',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid #e5e5e5',
  },
  additionalImagePreview: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  additionalRemoveBtn: {
    position: 'absolute',
    top: '6px',
    right: '6px',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.95)',
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
  },
  additionalAddBtn: {
    aspectRatio: '1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px dashed #e5e5e5',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    background: '#fafafa',
  },
};

export default BrandProductForm;
